"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";

export interface Transaction {
  id: string;
  transaction_type: "Sale" | "Rental";
  street_address: string;
  area: string;
  property_type: string;
  sale_price?: number;
  date_sold?: string;
  rental_amount?: number;
  rental_frequency?: "Monthly" | "Nightly";
  rental_term?: string;
  submitted_by: string;
  submitted_date: string;
  notes?: string;
}

interface TransactionCardProps {
  transaction: Transaction;
  onClick: (t: Transaction) => void;
  animationClass?: string;
  isAdmin?: boolean;
  onEdit?: (t: Transaction) => void;
  onDelete?: (id: string) => void;
}

function formatTTD(amount: number): string {
  return new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-TT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const PROPERTY_TYPE_BADGE: Record<string, string> = {
  House: "badge-house",
  Apartment: "badge-apartment",
  Townhouse: "badge-house",
  Land: "badge-land",
  Commercial: "badge-commercial",
  Other: "badge-other",
};

export default function TransactionCard({
  transaction: t,
  onClick,
  animationClass = "",
  isAdmin,
  onEdit,
  onDelete,
}: TransactionCardProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isSale = t.transaction_type === "Sale";
  const propBadge = PROPERTY_TYPE_BADGE[t.property_type] || "badge-other";

  return (
    <div
      onClick={() => onClick(t)}
      className={`bg-white border border-border rounded-lg p-5 cursor-pointer card-hover ${animationClass} opacity-0 flex flex-col`}
      style={{ animationFillMode: "forwards" }}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-navy text-sm leading-snug truncate">
            {t.street_address}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Icon name="MapPinIcon" size={12} className="text-muted flex-shrink-0" />
            <span className="text-xs text-muted truncate">{t.area}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span
            className={`text-2xs font-heading font-bold px-2.5 py-0.5 rounded-sm ${isSale ? "badge-sale" : "badge-rental"
              }`}
          >
            {t.transaction_type.toUpperCase()}
          </span>
          <span className={`text-2xs font-medium px-2 py-0.5 rounded-sm ${propBadge}`}>
            {t.property_type}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border my-3" />

      {/* Price row */}
      <div className="flex items-end justify-between flex-1">
        <div>
          {isSale ? (
            <>
              <p className="text-xs text-muted mb-0.5">Sale Price</p>
              <p className="font-heading font-bold text-navy text-lg leading-none">
                {t.sale_price ? formatTTD(t.sale_price) : "—"}
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-muted mb-0.5">
                Rental · {t.rental_frequency}
              </p>
              <p className="font-heading font-bold text-navy text-lg leading-none">
                {t.rental_amount ? formatTTD(t.rental_amount) : "—"}
              </p>
            </>
          )}
        </div>
        <div className="text-right">
          {isSale && t.date_sold && (
            <div className="flex items-center gap-1 text-xs text-muted">
              <Icon name="CalendarDaysIcon" size={12} />
              <span>{isMounted ? formatDate(t.date_sold) : "--"}</span>
            </div>
          )}
          {!isSale && t.rental_term && (
            <span className="text-2xs text-muted bg-sand px-2 py-0.5 rounded border border-border">
              {t.rental_term}
            </span>
          )}
        </div>
      </div>

      {/* Footer Meta */}
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-2xs text-muted">
          <Icon name="UserIcon" size={11} />
          <span>{t.submitted_by}</span>
        </div>
        <div className="flex items-center gap-1 text-2xs text-muted">
          <Icon name="ClockIcon" size={11} />
          <span>{isMounted ? formatDate(t.submitted_date) : "--"}</span>
        </div>
      </div>

      {/* Admin Actions Row */}
      {isAdmin && (
        <div className="mt-4 pt-3 border-t border-border-light flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(t);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-sand hover:bg-teal-muted text-navy hover:text-teal rounded text-2xs font-bold transition-all border border-border"
          >
            <Icon name="PencilSquareIcon" size={13} />
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(t.id);
            }}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-sand hover:bg-red-50 text-muted hover:text-red-600 rounded text-2xs font-bold transition-all border border-border"
          >
            <Icon name="TrashIcon" size={13} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
